/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import {
  Director,
  Stage,
  Events,
  AbstractEditor,
  CapDataDesc,
  GizmoEventType,
  ObjectClickedEvent,
  ObjectUnclickedEvent,
  GizmoTransformEvent,
} from '@sick-davinci/glow';
import {
  CrownViewCapData,
  CrownShapeCap,
  CrownViewPresentAddDesc,
} from '@sick-davinci/glow-crown';
import {
  CrownConnectionWebSocket,
  LogLevel,
  CrownProtocolJSON,
  PointerType,
  PointerActionType,
} from '@sick-davinci/device-communication';

import { debug, info, warn } from './log';
import { Object3D } from 'three';

/** connection dialog */
export default (async (stage: Stage, director: Director, getEditor: () => AbstractEditor,
                       url: string = '127.0.0.1:2111', viewId: string = '3D', setHandleShapeByEngine: (func) => void) => new Promise((resolve) => {

  // * Setup device connection with all necessary callbacks
  const cc = new CrownConnectionWebSocket(url, 'crownJSON', 3000);
  const cp = new CrownProtocolJSON();
  const view = new CrownViewCapData(cc, cp, viewId);

  const appLogInfo = (message: string) => view.logMessage(LogLevel.INFO, message);

  view.onConnectionChanged((connected) => {
    if (connected) {
      debug('onConnected');
      appLogInfo('hello from shape-editors example!');
      resolve();
    }
  });

  view.onPresentStart(() => {
    debug('onPresentStart');
    stage.lockRenderer();
  });

  view.onPresentEnd(() => {
    debug('onPresentEnd');
    stage.unlockRenderer();
  });

  view.onPresentClear(() => {
    debug('onPresentClear');
    getEditor().setObject(null);
    while (shapesFromEngine.length > 0) {
      shapesFromEngine.pop();
    }
    stage.clearTaggedObjects('clearable');
  });

  view.onPresentRemove((iconicId) => {
    debug('onPresentRemove:', iconicId);
  });

  view.onPresentEditor((type, iconicId) => {
    debug('onPresentEditor:', type, iconicId);
    // tslint:disable-next-line:switch-default
    switch (type) {
      case 'UNINSTALL': {
        const curObj = getEditor().getObject();
        if (curObj && curObj.name === iconicId) {
          getEditor().setObject(null);
        }
        break;
      }
      case 'INSTALL':
        const obj = stage.getObjectByName(iconicId);
        debug('install editor:', iconicId, obj);
        if (obj) {
          getEditor().setObject(obj);
        }
        break;
    }
  });

  const shapesFromEngine = new Array<string>();
  setHandleShapeByEngine( (obj: Object3D) => shapesFromEngine.includes(obj.name) );
  view.onPresentAddCapData((capData: CapDataDesc<CrownViewPresentAddDesc>) => {
    debug('onPresentAddCapData:', capData);

    if (capData.payload.Type !== CrownShapeCap.className) {  // only support shapes
      return;
    }

    info('CapData', capData);
    capData.tag = 'clearable';

    shapesFromEngine.push(capData.name);  // remember which shape came from the engine -> handle setting editor differently

    director.handleCapData(capData);
  });

  stage.addEventListener(Events.OBJECTCLICKED, ({detail}: CustomEvent<ObjectClickedEvent>) => {
    const { object } = detail.new;
    if (object && object.name && object !== getEditor().getObject()) {
      if (shapesFromEngine.includes(object.name)) { // notify engine if shape came from engine
        view.notifyPointerEvent(object.name, PointerActionType.CLICKED, PointerType.PRIMARY);
      }
    }
  });

  // * Stop editing the shape when unclicked
  stage.addEventListener(Events.OBJECTUNCLICKED, ({detail}: CustomEvent<ObjectUnclickedEvent>) => {
    const { object } = detail.old;
    if (shapesFromEngine.includes(object.name)) { // notify engine if shape came from engine
      view.notifyPointerEvent('', PointerActionType.CLICKED, PointerType.PRIMARY);
    }
  });

  stage.addEventListener(Events.GIZMOTRANSFORM, (event: CustomEvent<GizmoTransformEvent>) => {

    const {eventType, lastObject} = event.detail;
    if (GizmoEventType.DESELECTED === eventType && lastObject != null) {
      if (shapesFromEngine.includes(lastObject.name)) {
        view.notifyIconicChangedEvent(lastObject.name, lastObject.userData.format(lastObject));
      }
    }
  });

}));
